import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";
import { MdOutlineArrowLeft, MdOutlineArrowRight } from "react-icons/md";
import { CiFilter } from "react-icons/ci";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { Sidebar } from "primereact/sidebar";

import DynamicSVG from "../../components/dashboard/venue-table/dynmaic-svg";
import { useUsers } from "../../hooks/users";
import { deleteUser } from "../../api/admin-users";
import AddUserForm from "./AddUserForm";
import EditUserForm from "./EditUserForm";

// OverlayPanel text filter
const TextFilterPanel = ({
  opRef,
  label,
  value,
  onChange,
  onClear,
  onApply,
  placeholder = "Type to filter...",
}) => {
  const [isEditing, setIsEditing] = useState(false);
  return (
    <OverlayPanel ref={opRef} showCloseIcon dismissable={!isEditing} className="p-3 w-64">
      <div
        className="flex flex-col gap-2"
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onScroll={(e) => e.stopPropagation()}
      >
        <label className="text-xs text-gray-600">{label}</label>
        <input
          value={value ?? ""}
          onFocus={() => setIsEditing(true)}
          onBlur={() => setIsEditing(false)}
          onChange={(e) => {
            e.stopPropagation();
            onChange(e.target.value);
          }}
          className="border rounded px-2 py-2 text-sm"
          placeholder={placeholder}
        />
        <div className="flex gap-2 justify-end mt-2">
          <button
            className="px-3 py-1.5 text-sm rounded border bg-gray-50 hover:bg-gray-100"
            onClick={() => {
              onClear();
              opRef.current?.hide();
            }}
          >
            Clear
          </button>
          <button
            className="px-3 py-1.5 text-sm rounded bg-primary text-white hover:brightness-95"
            onClick={() => {
              onApply();
              opRef.current?.hide();
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </OverlayPanel>
  );
};

// Helpers
const getVendorsDisplay = (vendorIdArray) => {
  if (!Array.isArray(vendorIdArray) || vendorIdArray.length === 0) return "";
  const names = vendorIdArray.map((v) => v?.email || v?.name || "").filter(Boolean);
  return names.join(", ");
};

const ROLE_OPTIONS = [
  { name: "Super Admin", value: "SUPER_ADMIN" },
  { name: "Admin", value: "ADMIN" },
  { name: "User", value: "USER" },
];

const roleContants = {
  SUPER_ADMIN: "Super Admin",
  ADMIN: "Admin",
  USER: "User",
};
export const roleBadge = (role) => {
  const palette = {
    SUPER_ADMIN: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200" },
    ADMIN: { bg: "bg-sky-50", text: "text-sky-700", ring: "ring-sky-200" },
    USER: { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200" },
    DEFAULT: { bg: "bg-gray-50", text: "text-gray-700", ring: "ring-gray-200" },
  };
  const c = palette[role] || palette.DEFAULT;
  return (
    <span className={`inline-flex items-center rounded-full ${c.bg} ${c.text} ring-1 ${c.ring} px-2 py-0.5 text-[11px] leading-4 font-medium`}>
      {roleContants[role] || role}
    </span>
  );
};


const UsersList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [apiFilter, setApiFilter] = useState({ criteria: [] });


  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteUserItem, setDeleteUserItem] = useState(null);

  const { data: users, refetch, isLoading } = useUsers(currentPage, itemsPerPage, apiFilter);
  const totalPages = Math.ceil((users?.pagination?.totalCount || 0) / itemsPerPage);

  // Temporary password banner
  const [tempPassword, setTempPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (location.state?.tempPassword) {
      setTempPassword(location.state.tempPassword);
      setShowPassword(true);
      const timer = setTimeout(() => {
        setShowPassword(false);
        setTempPassword("");
      }, 20000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  useEffect(() => {
    refetch();
  }, [currentPage, itemsPerPage]);

  // API filter criteria model

  const upsertCriterion = (criteria, key, value) => {
    const filtered = criteria.filter((c) => c.key !== key);
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      filtered.push({ key, value, type: "regexOr" });
    }
    return filtered;
  };

  const applyFilterAndRefetch = (nextCriteria) => {
    setApiFilter({ criteria: nextCriteria });
    setCurrentPage(1);
    queueMicrotask(() => refetch());
  };

  // Filters + OverlayPanel refs
  const [nameFilter, setNameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");

  const nameOp = useRef(null);
  const emailOp = useRef(null);
  const roleOp = useRef(null);
  const vendorOp = useRef(null);

  // Vendor cell
  const vendorIdBodyTemplate = (row) => {
    const full = getVendorsDisplay(row?.vendorId);
    return (
      <span className="block text-xs md:text-sm max-w-[280px] whitespace-nowrap overflow-hidden text-ellipsis" title={full}>
        {full}
      </span>
    );
  };

  // Header filters (OverlayPanel)
  const NameHeader = (
    <div className="flex items-center gap-2 relative">
      <span>Name</span>
      <button
        type="button"
        onClick={(e) => nameOp.current?.toggle(e)}
        className={`p-1 rounded hover:bg-gray-100 border ${nameFilter ? "text-primary border-primary" : "text-gray-600 border-transparent"}`}
        aria-label="Filter by name"
        title="Filter by name"
      >
        <CiFilter className="w-4 h-4" />
      </button>
      <TextFilterPanel
        opRef={nameOp}
        label="Name"
        value={nameFilter}
        onChange={setNameFilter}
        onClear={() => {
          setNameFilter("");
          const next = upsertCriterion(apiFilter.criteria, "name", "");
          applyFilterAndRefetch(next);
        }}
        onApply={() => {
          const next = upsertCriterion(apiFilter.criteria, "name", nameFilter.trim());
          applyFilterAndRefetch(next);
        }}
        placeholder="Enter name"
      />
    </div>
  );

  const EmailHeader = (
    <div className="flex items-center gap-2 relative">
      <span>Email</span>
      <button
        type="button"
        onClick={(e) => emailOp.current?.toggle(e)}
        className={`p-1 rounded hover:bg-gray-100 border ${emailFilter ? "text-primary border-primary" : "text-gray-600 border-transparent"}`}
        aria-label="Filter by email"
        title="Filter by email"
      >
        <CiFilter className="w-4 h-4" />
      </button>
      <TextFilterPanel
        opRef={emailOp}
        label="Email"
        value={emailFilter}
        onChange={setEmailFilter}
        onClear={() => {
          setEmailFilter("");
          const next = upsertCriterion(apiFilter.criteria, "email", "");
          applyFilterAndRefetch(next);
        }}
        onApply={() => {
          const next = upsertCriterion(apiFilter.criteria, "email", emailFilter.trim());
          applyFilterAndRefetch(next);
        }}
        placeholder="user@domain.com"
      />
    </div>
  );


  const RoleHeader = (
    <div className="flex items-center gap-2 relative">
      <span>Role</span>
      <button
        type="button"
        onClick={(e) => roleOp.current?.toggle(e)}
        className={`p-1 rounded hover:bg-gray-100 border ${
          roleFilter ? "text-primary border-primary" : "text-gray-600 border-transparent"
        }`}
        aria-label="Filter by role"
        title="Filter by role"
      >
        <CiFilter className="w-4 h-4" />
      </button>

      {/* Custom overlay instead of TextFilterPanel */}
      <OverlayPanel ref={roleOp} showCloseIcon dismissable className="p-2 w-60">
        <div
          className="flex flex-col gap-2"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {/* Clear option */}
          <button
            type="button"
            className={`w-full text-left px-2 py-1.5 rounded border ${!roleFilter ? "bg-gray-100 border-gray-200" : "border-transparent hover:bg-gray-50"}`}
            onClick={() => {
              setRoleFilter("");
              const next = upsertCriterion(apiFilter.criteria, "role", "");
              applyFilterAndRefetch(next);
              roleOp.current?.hide();
            }}
          >
            All Roles
          </button>

          {/* Role chips as options */}
          <div className="grid grid-cols-1 gap-1">
            {ROLE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`flex items-center gap-2 justify-between px-2 py-1.5 rounded hover:bg-gray-50 border ${
                  roleFilter === opt.value ? "bg-gray-100 border-gray-200" : "border-transparent"
                }`}
                onClick={() => {
                  setRoleFilter(opt.value);
                  const next = upsertCriterion(
                    apiFilter.criteria.filter((c) => c.key !== "role"),
                    "role",
                    opt.value
                  );
                  applyFilterAndRefetch(next);
                  roleOp.current?.hide();
                }}
              >
                <span>{roleBadge(opt.value)}</span>
              </button>
            ))}
          </div>
        </div>
      </OverlayPanel>
    </div>
  );


  const VendorHeader = (
    <div className="flex items-center gap-2 relative">
      <span>Vendor Id</span>
      {/* <button
        type="button"
        onClick={(e) => vendorOp.current?.toggle(e)}
        className={`p-1 rounded hover:bg-gray-100 border ${vendorFilter ? "text-primary border-primary" : "text-gray-600 border-transparent"}`}
        aria-label="Filter by vendor"
        title="Filter by vendor"
      >
        <CiFilter className="w-4 h-4" />
      </button> */}
      <TextFilterPanel
        opRef={vendorOp}
        label="Vendor Name or Email"
        value={vendorFilter}
        onChange={setVendorFilter}
        onClear={() => {
          setVendorFilter("");
          const next = upsertCriterion(apiFilter.criteria, "vendor", "");
          applyFilterAndRefetch(next);
        }}
        onApply={() => {
          const next = upsertCriterion(apiFilter.criteria, "vendor", vendorFilter.trim());
          applyFilterAndRefetch(next);
        }}
        placeholder="Search vendor"
      />
    </div>
  );

  // Sidebar state
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [sidebarMode, setSidebarMode] = useState(null); // 'ADD' | 'EDIT'
  const [editingUserId, setEditingUserId] = useState(null);

  const openAddSidebar = () => {
    setSidebarMode("ADD");
    setEditingUserId(null);
    setSidebarVisible(true);
  };

  const openEditSidebar = (id) => {
    setSidebarMode("EDIT");
    setEditingUserId(id);
    setSidebarVisible(true);
  };

  const closeSidebar = () => {
    setSidebarVisible(false);
    setSidebarMode(null);
    setEditingUserId(null);
  };

  const onCreated = (tempPass) => {
    // show banner like original route-based flow
    setTempPassword(tempPass || "");
    setShowPassword(!!tempPass);
    setTimeout(() => {
      setShowPassword(false);
      setTempPassword("");
    }, 20000);
    closeSidebar();
    refetch();
  };

  const onUpdated = () => {
    closeSidebar();
    refetch();
  };

  const handleDeleteUser = async () => {
    try {
      const resp = await deleteUser(deleteUserItem._id);
      if (resp?.respMessage) toast.success(resp.respMessage);
      else toast.error(resp?.errorMessage || "Failed to delete user");
      setShowDeleteConfirmation(false);
      setDeleteUserItem(null);
      refetch();
    } catch {
      toast.error("Error deleting user");
      setShowDeleteConfirmation(false);
    }
  };

  const actionBodyTemplate = (rowData) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-pencil"
        className="p-button-rounded p-button-success p-button-sm"
        onClick={() => openEditSidebar(rowData._id)}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-danger p-button-sm text-red-700"
        onClick={() => {
          setDeleteUserItem(rowData);
          setShowDeleteConfirmation(true);
        }}
      />
    </div>
  );

  return (
    <div className="p-6 relative">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Admin Users</h1>
        {
          localStorage.getItem("role") === "SUPER_ADMIN" && (
            <button onClick={openAddSidebar} className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark">
              + Add New User
            </button>
          )
        }
      </div>

      {showPassword && (
        <div className="mb-4 flex items-center justify-between bg-yellow-100 border border-yellow-300 text-yellow-800 px-4 py-2 rounded-lg shadow-md">
          <span className="font-medium">Temporary Password: </span>
          <div className="flex items-center space-x-2">
            <span className="font-mono text-lg">{tempPassword}</span>
            <Button
              icon="pi pi-copy"
              className="p-button-rounded p-button-sm p-button-text text-gray-700 hover:text-gray-900"
              onClick={() => {
                navigator.clipboard.writeText(tempPassword);
                toast.success("Password copied to clipboard!");
              }}
            />
          </div>
        </div>
      )}

      <DataTable value={users?.adminUsers ?? []} loading={isLoading} stripedRows responsiveLayout="scroll">
        <Column
          header="SNo"
          body={(_, options) => (
            <DynamicSVG index={options.rowIndex} currentPage={currentPage} itemsPerPage={itemsPerPage} />
          )}
          className="p-2"
        />
        <Column field="name" header={NameHeader} className="py-4 text-xs md:text-sm" />
        <Column field="email" header={EmailHeader} className="py-4 text-xs md:text-sm" />
        <Column
          field="role"
          header={RoleHeader}
          className="py-4 text-xs md:text-sm"
          body={(rowData) => <div className="px-2">{roleBadge(rowData.role)}</div>}
        />
        <Column field="vendorId" header={VendorHeader} body={vendorIdBodyTemplate} className="py-4 text-xs md:text-sm" />
        <Column
          field="createdAt"
          header="Joined"
          className="py-4 text-xs md:text-sm"
          body={(rowData) => moment(rowData.createdAt).format("DD-MM-YYYY")}
        />
        {
          localStorage.getItem("role") === "SUPER_ADMIN" && (
            <Column
              header="Actions"
              body={actionBodyTemplate}
              style={{ textAlign: "center", width: "8rem" }}
              className="py-4 text-xs md:text-sm"
            />
          )
        }
      </DataTable>

      {/* Pagination */}
      <div className="flex justify-center items-center gap-5 mt-6">
        <span className="text-black text-sm">Page</span>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((prev) => prev - 1)}
            disabled={currentPage === 1}
            className={`disabled:opacity-50 ${currentPage === 1 ? "cursor-not-allowed" : ""}`}
          >
            <MdOutlineArrowLeft className="text-primary text-2xl" />
          </button>
          <div className="min-w-[32px] h-[32px] flex bg-primary text-white justify-center items-center rounded-full text-sm font-bold">
            {currentPage}
          </div>
          <button
            onClick={() => setCurrentPage((prev) => prev + 1)}
            disabled={currentPage === totalPages}
            className={`disabled:opacity-50 ${currentPage === totalPages ? "cursor-not-allowed" : ""}`}
          >
            <MdOutlineArrowRight className="text-primary text-2xl" />
          </button>
        </div>

        <select
          value={itemsPerPage}
          onChange={(e) => {
            setCurrentPage(1);
            setItemsPerPage(parseInt(e.target.value, 10));
          }}
          className="py-2 px-3 bg-white border border-blue-200 rounded text-sm"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={30}>30</option>
        </select>
      </div>

      {/* Delete modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-">
          <div className="bg-white rounded-2xl shadow-xl w-[90%] max-w-sm p-6 animate-fadeIn">
            <h2 className="text-lg font-semibold text-gray-800">Delete User</h2>
            <p className="text-gray-600 text-sm mt-2">
              Are you sure you want to delete <span className="font-semibold">{deleteUserItem?.name}</span>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setDeleteUserItem(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                onClick={handleDeleteUser}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Right Sidebar for Add/Edit forms */}
      <Sidebar
        visible={sidebarVisible}
        position="right"
        onHide={closeSidebar}
        className="w-full sm:w-[520px] md:w-[640px]"
        dismissable
        modal
      >
        {sidebarMode === "ADD" && (
          <AddUserForm
            onCreated={(tempPass) => onCreated(tempPass)}
            onCancel={closeSidebar}
          />
        )}
        {sidebarMode === "EDIT" && editingUserId && (
          <EditUserForm
            userId={editingUserId}
            onUpdated={onUpdated}
            onCancel={closeSidebar}
          />
        )}
      </Sidebar>
    </div>
  );
};

export default UsersList;






      